// components/DegreeAudit/Progress/RequirementFulfillmentBar.tsx

import { memo } from 'react';

export interface AreaFulfillment {
  areas_total: number;
  areas_completed: number;
  areas_in_progress: number;
  areas_planned: number;
  areas_suggested: number;
  areas_available: number;
}

export interface ProgramWithFulfillment {
  area_fulfillment?: AreaFulfillment;
  [key: string]: unknown;
}

export interface RequirementFulfillmentBarProps {
  program: ProgramWithFulfillment;
}

/**
 * Single progress bar showing requirement fulfillment for majors/minors/citations.
 * Tracks how many sub-requirements are fulfilled rather than credits,
 * since per-program credits are already shown in the overall degree header.
 *
 * UX-R1 task 2.3: one compact numeric ("N of M requirements") + the bar;
 * the always-on per-status legend was redundant with both and is replaced
 * by per-segment tooltips + an aria summary on the bar.
 */
const RequirementFulfillmentBar = memo(({ program }: RequirementFulfillmentBarProps) => {
  const af = program.area_fulfillment;
  if (!af || af.areas_total === 0) return null;

  const total = af.areas_total;
  const segments = {
    completed: (af.areas_completed / total) * 100,
    in_progress: (af.areas_in_progress / total) * 100,
    planned: (af.areas_planned / total) * 100,
    suggested: (af.areas_suggested / total) * 100,
    available: (af.areas_available / total) * 100,
  };

  const fulfilled =
    af.areas_completed + af.areas_in_progress + af.areas_planned + af.areas_suggested;

  const ariaSummary =
    `${af.areas_completed} of ${total} requirements completed` +
    (af.areas_in_progress ? `, ${af.areas_in_progress} in progress` : '') +
    (af.areas_planned ? `, ${af.areas_planned} planned` : '') +
    (af.areas_suggested ? `, ${af.areas_suggested} suggested` : '') +
    (af.areas_available ? `, ${af.areas_available} unfulfilled` : '');

  return (
    <div className="w-full">
      <div className="text-xs text-ink-3 mb-1 text-right">
        {af.areas_completed} of {total} requirements
        {fulfilled > af.areas_completed && ` (${fulfilled} with plan)`}
      </div>

      <div
        data-testid="fulfillment-bar"
        role="img"
        aria-label={ariaSummary}
        className="flex h-3 bg-surface-sunken rounded-full overflow-hidden"
      >
        {segments.completed > 0 && (
          <div
            className="bg-green-500 transition-all duration-300"
            title={`Completed (${af.areas_completed})`}
            style={{ width: `${segments.completed}%` }}
          />
        )}
        {segments.in_progress > 0 && (
          <div
            className="bg-blue-500 transition-all duration-300"
            title={`In progress (${af.areas_in_progress})`}
            style={{ width: `${segments.in_progress}%` }}
          />
        )}
        {segments.planned > 0 && (
          <div
            className="bg-yellow-500 transition-all duration-300"
            title={`Planned (${af.areas_planned})`}
            style={{ width: `${segments.planned}%` }}
          />
        )}
        {segments.suggested > 0 && (
          <div
            className="bg-status-suggested/50 transition-all duration-300"
            title={`AI-suggested (${af.areas_suggested})`}
            style={{ width: `${segments.suggested}%` }}
          />
        )}
        {segments.available > 0 && (
          <div
            className="bg-line-soft transition-all duration-300"
            title={`Unfulfilled (${af.areas_available})`}
            style={{ width: `${segments.available}%` }}
          />
        )}
      </div>
    </div>
  );
});

RequirementFulfillmentBar.displayName = 'RequirementFulfillmentBar';

export default RequirementFulfillmentBar;
